import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import '../PartnerReferalRewardModal/PartnerReferalRewardModal.css';
// Кнопка входа — ровно та же, что была на странице авторизации (ее классы)
import '../../YandexAuthPage/YandexAuthPage.css';
import './LoginRequiredModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {goToYandexAuth} from '../../../utils/auth/goToYandexAuth';
import {LOGIN_REQUIRED_MESSAGES, LOGIN_REQUIRED_REASONS} from '../../../configs/guestProfile';
import {sendPageEvent} from '../../../analytics/gameAnalytics';
import {getDeviceKind} from '../../../utils/device/getDeviceKind';
import EmailAuthForm from '../../YandexAuthPage/EmailAuthForm';

const PAGE_ID = 'LoginRequiredModal';

/**
 * Модалка входа для гостя (см. configs/guestProfile.js): открывается по клику
 * на шапку профиля и по тем кнопкам меню, которые без аккаунта работать не
 * могут. Заголовок — под конкретную кнопку, чтобы человек понимал, зачем
 * именно сейчас предлагают войти.
 *
 * Стиль — модалки реферальной награды (ее же классы), кнопки — те же самые,
 * что были на странице авторизации, и делают ровно то же самое.
 */
function LoginRequiredModal({
  reason = LOGIN_REQUIRED_REASONS.HEADER,
  onClose,
  onDevLogin,
  onEmailAuthSuccess,
}) {
  const [visible, setVisible] = useState(false);
  // Форма почты раскрывается на месте кнопок входа: отдельная модалка поверх
  // модалки не нужна, а вернуться к выбору способа входа надо в один клик
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const isDev = process.env.NODE_ENV === 'development';
  const message = LOGIN_REQUIRED_MESSAGES[reason] || LOGIN_REQUIRED_MESSAGES[LOGIN_REQUIRED_REASONS.HEADER];

  useEffect(() => {
    // Показ модалки — ключевая точка воронки гостя: видно, сколько человек
    // вообще дошло до предложения войти, с какой кнопки и с какого устройства.
    // Тип устройства здесь про РАСКЛАДКУ, которую человек видел, а платформу
    // (ios/android/windows) GameAnalytics пишет к событиям сам
    sendPageEvent('LoginRequiredModal', 'mount', reason, getDeviceKind());
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  // Модалка рендерится ПОРТАЛОМ в body, а не на месте вызова. Иначе она
  // достается в наследство контекст наложения родителя: из шапки профиля ее
  // открывает UserHeader, а .main-menu-header — position + z-index, то есть
  // собственный контекст. Внутри него любой z-index модалки уже не значит
  // ничего: вся ветка целиком рисуется на уровне шапки, ниже оверлеев витрины
  // (баннер конца матча, тост политики, представление игроков). Портал
  // выносит модалку в корневой контекст, и она перекрывает их независимо от
  // того, откуда ее позвали
  return createPortal(
    <div
      className={`partner-referal-reward-overlay login-required-modal-overlay ${visible ? 'partner-referal-reward-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role={'dialog'}
      aria-modal={'true'}
    >
      <div className={`partner-referal-reward-modal ${visible ? 'partner-referal-reward-modal_visible' : ''}`}>
        <h2 className={'partner-referal-reward-title login-required-modal-title'}>{message}</h2>

        <div className={'main-menu-buttons partner-referal-reward-buttons'}>
          {isEmailFormOpen ? (
            <EmailAuthForm
              pageId={PAGE_ID}
              onAuthSuccess={(data) => {
                // Кука уже проставлена сервером — модалку закрываем и отдаем
                // управление наверх (там же разбирается новичок с ником).
                // onClose — как у обычного закрытия: вошедший в существующий
                // аккаунт остается на том же экране, и без него невидимый
                // оверлей модалки висел бы поверх и глотал клики, а меню
                // считало бы ее открытой (очередь его модалок стояла бы)
                setVisible(false);
                onEmailAuthSuccess?.(data);
                setTimeout(() => onClose?.(), 300);
              }}
              onCancel={() => setIsEmailFormOpen(false)}
            />
          ) : (
          <>
          <PageButton
            onClick={() => setIsEmailFormOpen(true)}
            pageId={PAGE_ID}
            buttonId={'email-login-open'}
          >
            {/* Раскладка и размер значка — те же классы, что у кнопки Яндекса:
                кнопки стоят рядом и обязаны выглядеть одинаково */}
            <span className={'yandex-auth-button-content'}>
              <img className={'yandex-auth-icon'} src={'/img/icons/email.png'} alt={''}/>
              Войти по email
            </span>
          </PageButton>

          <PageButton onClick={goToYandexAuth} pageId={PAGE_ID} buttonId={'yandex-login'}>
            <span className={'yandex-auth-button-content'}>
              <img className={'yandex-auth-icon'} src={'/img/icons/yandex.png'} alt={''}/>
              Войти c
              <span className={'yandex-auth-service-name'}>Яндекс ID</span>
            </span>
          </PageButton>

          {isDev && (
            <PageButton onClick={onDevLogin} pageId={PAGE_ID} buttonId={'dev-login'}>
              Войти dev
            </PageButton>
          )}

          <PageButton
            className={'main-menu-button_secondary'}
            onClick={handleClose}
            pageId={PAGE_ID}
            buttonId={'close'}
          >
            Не сейчас
          </PageButton>
          </>
          )}
        </div>

        <p className={'login-required-modal-hint'}>
          Если испытываете проблемы с авторизацией, то попробуйте выключить VPN или, наоборот, включить.
        </p>
      </div>
    </div>,
    document.body
  );
}

export default LoginRequiredModal;
